import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { LoadingPanel } from "../LoadingPanel";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { CommandCell } from '../forms/CommandCell';
import { getCSRFToken } from '../../app/getCSRFToken';
import { Button } from "@progress/kendo-react-buttons";

export const AdministratorTicketingWebsites = () => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [originalTicketingWebsites, setOriginalTicketingWebsites] = useState([]);
    const [areTicketingWebsitesLoading, setAreTicketingWebsitesLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");

    const getTicketingWebsites = () => {
        setAreTicketingWebsitesLoading(true);

        fetch(`/api/administrator/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setAreTicketingWebsitesLoading(false);
                setTicketingWebsites(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreTicketingWebsitesLoading(false);
                setLoadingError(error.message);
            });
    }

    const updateTicketingWebsite = (dataItem) => {
        fetch("/api/administrator/ticketingwebsite",
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Updated item successfully");

                getTicketingWebsites();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            edit={enterEdit}
            canDelete={false}
            discard={discard}
            update={update}
            cancel={cancel}
            editField={editField}
            keyField="ticketingWebsiteId"
        />
    );

    const update = (dataItem) => {
        updateTicketingWebsite(dataItem);
    };

    const discard = (dataItem) => {
        const newTicketingWebsites = [...ticketingWebsites];
        newTicketingWebsites.splice(0, 1);
        setTicketingWebsites(newTicketingWebsites);
    };

    const cancel = (dataItem) => {
        const originalItem = originalTicketingWebsites.find(
            (p) => p.ticketingWebsiteId === dataItem.ticketingWebsiteId
        );

        const newTicketingWebsites = ticketingWebsites.map((item) =>
            item.ticketingWebsiteId === dataItem.ticketingWebsiteId ? originalItem : item
        );
        setTicketingWebsites(newTicketingWebsites);
    };

    const enterEdit = (dataItem) => {
        const isEditingTicketingWebsite = ticketingWebsites.some(
            (p) => p.inEdit === true
        );

        if (isEditingTicketingWebsite === true) {
            addNotificationWithTimeout(dispatch, "error", "You can only edit one item at a time");
            return;
        }

        setOriginalTicketingWebsites(ticketingWebsites);

        const newTicketingWebsites = ticketingWebsites.map((item) =>
            item.ticketingWebsiteId === dataItem.ticketingWebsiteId ? { ...item, inEdit: true } : item
        );
        setTicketingWebsites(newTicketingWebsites);
    };

    const itemChange = (event) => {
        const field = event.field || "";
        const newTicketingWebsites = ticketingWebsites.map((item) =>
            item.ticketingWebsiteId === event.dataItem.ticketingWebsiteId
                ? { ...item, [field]: event.value }
                : item
        );
        setTicketingWebsites(newTicketingWebsites);
    };

    const updateShortingStatus = (ticketingWebsiteId, isShortingDisabled) => {
        const dataItem = {
            ticketingWebsiteId: ticketingWebsiteId,
            isShortingDisabled: isShortingDisabled,
        };

        fetch(`/api/administrator/ticketingwebsiteshortingstatus`,
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Updated shorting status");

                getTicketingWebsites();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const UpdateShortingStatusCell = (props) => {
        const ticketingWebsiteId = props.dataItem.ticketingWebsiteId;
        const isShortingDisabled = props.dataItem.isShortingDisabled;

        return (
            <td>
                <Button
                    type={"button"}
                    onClick={() => updateShortingStatus(ticketingWebsiteId, !isShortingDisabled)}
                    themeColor={!isShortingDisabled ? undefined : "primary"}
                    icon={!isShortingDisabled ? "close" : "plus"}>
                    {!isShortingDisabled ? "Stop Shorting" : "Start Shorting"}
                </Button>
            </td>
        );
    };

    useEffect(() => {
        document.title = "Ticketing Websites";

        getTicketingWebsites();
    }, []);

    return (
        <>
            { areTicketingWebsitesLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <Grid
                    data={ticketingWebsites}
                    onItemChange={itemChange}
                    editField={editField}
                    dataItemKey="ticketingWebsiteId"
                    resizable={true}
                    scrollable="none"
                >
                    <Column cell={MyCommandCell} width="200px" />
                    <Column field="ticketingWebsiteId" title={"Id"} editable={false} />
                    <Column field="name" title={"Name"} editable={false} />
                    <Column field="isActive" title={"Active"} editor="boolean" />
                    <Column field="hasIPAddressRestrictions" title={"Has IP Restrictions"} editor="boolean" />
                    <Column field="createAlertsForNewEvents" title={"Create Alerts for New Events"} editor="boolean" />    
                    <Column field="defaultShortingQuantity" title={"Default Shorting Quantity"} editor="numeric" />
                    <Column cell={UpdateShortingStatusCell} />
                </Grid>
                ) : <></>)
            }
        </>
    );
}